import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { isAppShell } from '../../app-shell/is-app-shell.utility';
import { WorkloadQueryParameter } from '../constants/workload-query-parameter.enum';
import { FileItem, FileType } from '../models/file-item.model';
import { ConfigParserService } from '../services/config-parser.service';
import { DeviceDetector, PlatformType } from '../services/device-detector.service';
import { FeatureManagerService } from '../services/feature-manager.service';
import { WebPwaService } from '../services/web-pwa.service';
import { WindowService } from '../services/window.service';

export interface CapabilitiesConfig {
    accountSwitchingEnabled: boolean;
    activityBasedTimeoutEnabled: boolean;
    addToDoEnabled: boolean;
    appBarEnabled: boolean;
    appBarOverflowOnlyEnabled: boolean;
    appBarAdminTileEnabled: boolean;
    appBarFlyoutAddAppsStoreLinkEnabled: boolean;
    appBarFlyoutMoreAppsGalleryLinkEnabled: boolean;
    appBarMicaStylesEnabled: boolean;
    appBarDragDropEnabled: boolean;
    appBarMSAAppsInFlyoutEnabled: boolean;
    appBarPinningEnabled: boolean;
    appsModuleEnabled: boolean;
    appManifestEnabled: boolean;
    appsInHero: boolean;
    altaBrandingChangesEnabled: boolean;
    compliantShareApiEnabled: boolean;
    contextMenu20Enabled: boolean;
    convertToPdfEnabled: boolean;
    bottomTrayEnabled: boolean;
    calendarEnabled: boolean;
    capPopupEnabled: boolean;
    captureScanEnabled: boolean;
    clientTokenFetchEnabled: boolean;
    contentActionsEnabled: boolean;
    contentActionsOHomeEnabled: boolean;
    contentLauncherEnabled: boolean;
    createBackgroundEnabled: boolean;
    createCenterEnabled: boolean;
    createCenterReactEnabled: boolean;
    createClipchampEnabled: boolean;
    createDesignerEnabled: boolean;
    createTemplatesSeeMoreEnabled: boolean;
    createDocAspxExcelEnabled: boolean;
    createDocAspxPowerPointEnabled: boolean;
    createDocAspxVisioEnabled: boolean;
    createDocAspxWordEnabled: boolean;
    createDocumentSupported: boolean;
    createDynamicOrderingEnabled: boolean;
    createOneDriveDocApiEnabled: boolean;
    createOneNotePageEnabled: boolean;
    createOnHomeEnabled: boolean;
    createOnHomeReactEnabled: boolean;
    createOnHomeListsEnabled: boolean;
    createOnHomeDesignerEnabled: boolean;
    createOnHomeHeaderTextRefreshEnabled: boolean;
    createPaneSvgsEnabled: boolean;
    createScreenRecordingDocumentEnabled: boolean;
    createSharingLinkApiEnabled: boolean;
    createStreamDocumentEnabled: boolean;
    createStreamPlaylistEnabled: boolean;
    createStreamRecorderEnabled: boolean;
    createPlaylistWithNewUrlEnabled: boolean;
    createVisioEnabled: boolean;
    clientPwasEnabled: boolean;
    darkModeEnabled: boolean;
    darkModeOverride: boolean;
    defaultHeaderLayoutEnabled: boolean;
    delayedServiceWorkerInit: boolean;
    deleteActionEnabled: boolean;
    dimeBrokerEnabled: boolean;
    disableAnimation: boolean;
    downloadActionEnabled: boolean;
    draftEnabled: boolean;
    earlierStreamPlacementEnabled: boolean;
    edgeworthEnabled: boolean;
    edgeworthReactEnabled: boolean;
    embedAppCachingEnabled: boolean;
    embedHost: string;
    embedModeEnabled: boolean;
    embedThemingEnabled: boolean;
    favoriteTemplatesEnabled: boolean;
    featureFlagsEnabled: boolean;
    firstDraftFeedbackEnabled: boolean;
    firstRunEnabled: boolean;
    firstRunReactEnabled: boolean;
    fluentHeader: boolean;
    fluentRefresh: boolean;
    fluentIconFontEnabled: boolean;
    fluidAudience: string;
    fluidCapPopupEnabled: boolean;
    fluidCreateAbpEnabled: boolean;
    fluidCreateNewEnabled: boolean;
    fluidFirst: boolean;
    fluidMipEnabled: boolean;
    fluidMockMipEnabled: boolean;
    fluidSupportEnabled: boolean;
    fluidUseServiceDeliveredDriver: boolean;
    fluidVersionHistoryEnabled: boolean;
    formsCapLoginEnabled: boolean;
    gettingToKnowYouEnabled: boolean;
    heroGradientEnabled: boolean;
    homeReorderedEnabled: boolean;
    hoverPreloadTelemetryEnabled: boolean;
    isConsumerUser: boolean;
    isEmbedMode: boolean;
    isPwaInUwp: boolean;
    isPwaInWebView2: boolean;
    installButtonReactEnabled: boolean;
    installButtonReactMenuForConsumerEnabled: boolean;
    irisForLearningShelfEnabled: boolean;
    learningShelfEnabled: boolean;
    leftNavCalloutEnabled: boolean;
    leftNavReact: boolean;
    leftNavEmptyImage: string;
    limitNetworkRequests: boolean;
    listsCreateEnabled: boolean;
    lorByDefaultEnabled: boolean;
    maxInitialPlaces: number;
    maxTotalPlaces: number;
    memoryProfilingEnabled: boolean;
    metaExtensionsFetchEnabled: boolean;
    mobileContextMenusEnabled: boolean;
    mobileCustomSearchEnabled: boolean;
    msaClientTokensEnabled: boolean;
    m365AppsAboutEnabled: boolean;
    m365AppsEnabled: boolean;
    m365DefaultThemeEnabled: boolean;
    m365TestAppEnabled: boolean;
    mosAppsHostedAtRoot: string;
    mosAppHostedAtRootEnabled: boolean;
    feedEnabled: boolean;
    modernStartAppsArray: Array<string>;
    modernStartApps?: Set<FileType>;
    modernStartPageEnabled: boolean;
    modernStreamStartPageEnabled: boolean;
    modernVisioStartPageEnabled: boolean;
    modernLoopStartPageEnabled: boolean;
    modernWhiteboardStartPageEnabled: boolean;
    modernDesignerStartPageEnabled: boolean;
    modernClipChampStartPageEnabled: boolean;
    modernStartPageRootPath: string;
    modernTemplatesAppsArray: Array<string>;
    modernTemplatesPageEnabled: boolean;
    myContentEnabled: boolean;
    myContentGridViewEnabled: boolean;
    myContentUsqEnabled: boolean;
    m365AppsPageRootPath: string;
    nativeSearchInHeaderEnabled: boolean;
    newConsumerExperienceEnabled: boolean;
    newConsumerOverride: boolean;
    newMruEmptyStateEnabled: boolean;
    newUserShelfEnabled: boolean;
    newUserShelfOverride: boolean;
    northStarEnabled: boolean;
    officeHomeFloodgate: boolean;
    officeStartUsedThirdPartyAppsEnabled: boolean;
    oneDriveApisEnabled: boolean;
    oneDriveUploadEnabled: boolean;
    otherFlights: string;
    outgoingLoginHintEnabled: boolean;
    parkEnabled: boolean;
    peopleCardsEnabled: boolean;
    peopleListViewEnabled: boolean;
    pinningPeopleEnabled: boolean;
    powerBiCreateEnabled: boolean;
    prefetchDataEnabled: boolean;
    pwaEnabled: boolean;
    quickAccessV1Enabled: boolean;
    quickAccessV1GridViewEnabled: boolean;
    quickAccessV1CustomPivotEnabled: boolean;
    reactAppToggleEnabled: boolean;
    recommendedDeepLinkEnabled: boolean;
    recommendedFloodgate: boolean;
    recommendedReact: boolean;
    recommendedTeamsLinkEnabled: boolean;
    redirectWhenSignedOutFromWorkloads: boolean;
    reloadWhenAuthExpired: boolean;
    reloadWhenPolicyCheckFailed: boolean;
    reloadWhenReauthRequired: boolean;
    remoteModulesEnabled: boolean;
    replacePinActionEnabled: boolean;
    scheduleEnabled: boolean;
    secondaryTokensEnabled: boolean;
    secondRunSREConsumerEnabled: boolean;
    sharedServicesEnabled: boolean;
    sharePointEnabled: boolean;
    shareTokenClaimsEnabled: boolean;
    shareToTeamsEnabled: boolean;
    shellDocumentTitleEnabledInFluid: boolean;
    shellFeedbackEnabled: boolean;
    shellPinEnabled: boolean;
    showA2HSPrompt: boolean;
    showBusinessApps: boolean;
    showFeedback: boolean;
    showFloodgate: boolean;
    showFolders: boolean;
    showPwaFeelingsSurvey: boolean;
    showSearch: boolean;
    sideloadAppButtonEnabled: boolean;
    spfxAppEnabled: boolean;
    storeAppEnabled: boolean;
    startPageAppsArray: Array<string>;
    startPageApps?: Set<FileType>;
    startPageRootPath: string;
    stickyPagesEnabled: boolean;
    suggestedActionsEnabled: boolean;
    supportCentralEnabled: boolean;
    svgAppIconsEnabled: boolean;
    swayCreateNewEnabled: boolean;
    surfaceExpEnabled: boolean;
    teachingUiEnabled: boolean;
    teachingUiReactEnabled: boolean;
    teachingUiReactNewUserEnabled: boolean;
    templatePageRootPath: string;
    templatesEnabled: boolean;
    themeOverridesEnabled: boolean;
    thumbnailEndpointEnabled: boolean;
    threeSRdlFlightHeader: string;
    threeSRdlFeedExperiment: string;
    toDoM365AppEnabled: boolean;
    unifiedHostPath: string;
    unityEnabled: boolean;
    unityCalloutEnabled: boolean;
    universalMeControlPWAEnabled: boolean;
    universalMeControlSafetyFlightEnabled: boolean;
    upgradeMosAppEnabled: boolean;
    uploadV2Enabled: boolean;
    streamUploadV2Enabled: boolean;
    userConsentCallEnabled: boolean;
    userPreferenceEnabled: boolean;
    useVroomForOneDriveApis: boolean;
    wacExcelJsApiV2Enabled: boolean;
    wacHostAppsArray: Array<string>;
    wacHostPrebootAppsArray: Array<string>;
    wacHostTimeoutTeardownEnabled: boolean;
    wacHostEnabled: boolean;
    wacHostControlFlight: boolean;
    wacHostHoverPreloadEnabled: boolean;
    wacHostApps?: Set<FileType>;
    wacHostPrebootApps?: Set<FileType>;
    wacHostPagePath: string;
    wacSharingLinkEnabled: boolean;
    wacStartPageEnabled: boolean;
    wacHostExternalLinkEnabled: boolean;
    wacHostOrgTemplatesEnabled: boolean;
    wacHostGetAuthTokenEnabled: boolean;
    wacHostGetCustomAuthConfigEnabled: boolean;
    wacWordJsApiV2Enabled: boolean;
    wacHostMoveToFolderEnabled: boolean;
    webVitalsEnabled: boolean;
    formsCalloutEnabled: boolean;
    scenarioId: string;
    wacHostHoverPrebootAppsArray: Array<string>;
    wacHostHoverPrebootApps?: Set<FileType>;
    wacHostHoverPrebootControlFlight: boolean;
    yammerCreateEnabled: boolean;
    yammerM365AppEnabled: boolean;
    whiteboardCreateEnabled: boolean;
    greetingRefreshEnabled: boolean;
    adminPolicyEnabled: boolean;
    visualAppsModuleEnabled: boolean;
    visualBackgroundEnabled: boolean;
    visualBackgroundGradientEnabled: boolean;
    visualCreateCenterModuleEnabled: boolean;
    visualEnabled: boolean;
    visualFeedModuleEnabled: boolean;
    visualMyContentModuleEnabled: boolean;
    visualSettingEnabled: boolean;
    wbShowStoragePercentageEnabled: boolean;
    workloadPublicDataEnabled: boolean;
    welcomeBadgeEnabled: boolean;
    welcomeBadgeV2Enabled: boolean;
    claimsChallengeEnabled: boolean;
    m365StringLogoEnabled: boolean;
    m365MobileDesktopRebrandEnabled: boolean;
    portalPreloadEnabled: boolean;
    announcementEnabled: boolean;
    useUSQEnabled: boolean;
    showEnforcementEnabled: boolean;
    hybridAuthEnabled: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class CapabilitiesService {
    public readonly openWithUIHost: Observable<boolean>;
    public readonly capabilities: CapabilitiesConfig;

    private shouldOpenWithUIHost = false;

    constructor(
        deviceDetector: DeviceDetector,
        configParserService: ConfigParserService,
        private readonly featureManagerService: FeatureManagerService,
        webPwaService: WebPwaService,
        windowService: WindowService
    ) {
        // Disable all capabilities if there was failure to read them from page response
        this.capabilities = configParserService.parse<CapabilitiesConfig>('capabilities') || {
            accountSwitchingEnabled: false,
            adminPolicyEnabled: false,
            activityBasedTimeoutEnabled: false,
            addToDoEnabled: false,
            appBarEnabled: false,
            appBarOverflowOnlyEnabled: false,
            appBarAdminTileEnabled: false,
            appBarFlyoutAddAppsStoreLinkEnabled: false,
            appBarFlyoutMoreAppsGalleryLinkEnabled: false,
            appBarMicaStylesEnabled: false,
            appBarDragDropEnabled: false,
            appBarMSAAppsInFlyoutEnabled: false,
            appBarPinningEnabled: false,
            appsModuleEnabled: false,
            appManifestEnabled: false,
            appsInHero: false,
            altaBrandingChangesEnabled: false,
            bottomTrayEnabled: false,
            calendarEnabled: false,
            capPopupEnabled: false,
            captureScanEnabled: false,
            clientTokenFetchEnabled: false,
            contentActionsEnabled: false,
            contentActionsOHomeEnabled: false,
            contentLauncherEnabled: false,
            compliantShareApiEnabled: false,
            contextMenu20Enabled: false,
            convertToPdfEnabled: false,
            createBackgroundEnabled: false,
            createCenterEnabled: false,
            createCenterReactEnabled: false,
            createClipchampEnabled: false,
            createDesignerEnabled: false,
            createTemplatesSeeMoreEnabled: false,
            createDocAspxExcelEnabled: false,
            createDocAspxPowerPointEnabled: false,
            createDocAspxVisioEnabled: false,
            createDocAspxWordEnabled: false,
            createDocumentSupported: false,
            createDynamicOrderingEnabled: false,
            createOneDriveDocApiEnabled: false,
            createOneNotePageEnabled: false,
            createOnHomeEnabled: false,
            createOnHomeReactEnabled: false,
            createOnHomeListsEnabled: false,
            createOnHomeDesignerEnabled: false,
            createOnHomeHeaderTextRefreshEnabled: false,
            createPaneSvgsEnabled: false,
            createSharingLinkApiEnabled: false,
            createStreamDocumentEnabled: false,
            createStreamPlaylistEnabled: false,
            createStreamRecorderEnabled: false,
            createScreenRecordingDocumentEnabled: false,
            createPlaylistWithNewUrlEnabled: false,
            createVisioEnabled: false,
            clientPwasEnabled: false,
            darkModeEnabled: false,
            darkModeOverride: false,
            defaultHeaderLayoutEnabled: false,
            delayedServiceWorkerInit: false,
            deleteActionEnabled: false,
            downloadActionEnabled: false,
            dimeBrokerEnabled: false,
            disableAnimation: false,
            draftEnabled: false,
            earlierStreamPlacementEnabled: false,
            edgeworthEnabled: false,
            edgeworthReactEnabled: false,
            embedAppCachingEnabled: false,
            embedHost: '',
            embedModeEnabled: false,
            embedThemingEnabled: false,
            favoriteTemplatesEnabled: false,
            featureFlagsEnabled: false,
            firstDraftFeedbackEnabled: false,
            firstRunEnabled: false,
            firstRunReactEnabled: false,
            fluentHeader: false,
            fluentRefresh: false,
            fluentIconFontEnabled: false,
            fluidAudience: 'Unknown',
            fluidCapPopupEnabled: false,
            fluidCreateAbpEnabled: false,
            fluidCreateNewEnabled: false,
            fluidFirst: false,
            fluidMipEnabled: false,
            fluidMockMipEnabled: false,
            fluidSupportEnabled: false,
            fluidUseServiceDeliveredDriver: false,
            fluidVersionHistoryEnabled: false,
            formsCalloutEnabled: false,
            formsCapLoginEnabled: false,
            gettingToKnowYouEnabled: false,
            greetingRefreshEnabled: false,
            heroGradientEnabled: false,
            homeReorderedEnabled: false,
            hoverPreloadTelemetryEnabled: false,
            isConsumerUser: false,
            isEmbedMode: false,
            isPwaInUwp: false,
            isPwaInWebView2: false,
            installButtonReactEnabled: false,
            installButtonReactMenuForConsumerEnabled: false,
            irisForLearningShelfEnabled: false,
            learningShelfEnabled: false,
            leftNavCalloutEnabled: false,
            leftNavReact: false,
            leftNavEmptyImage: '',
            limitNetworkRequests: false,
            listsCreateEnabled: false,
            lorByDefaultEnabled: false,
            maxInitialPlaces: 0,
            maxTotalPlaces: 0,
            memoryProfilingEnabled: false,
            metaExtensionsFetchEnabled: false,
            mobileContextMenusEnabled: false,
            mobileCustomSearchEnabled: false,
            msaClientTokensEnabled: false,
            m365AppsAboutEnabled: false,
            m365AppsEnabled: false,
            m365DefaultThemeEnabled: false,
            m365TestAppEnabled: false,
            mosAppsHostedAtRoot: '',
            mosAppHostedAtRootEnabled: false,
            feedEnabled: false,
            modernStartAppsArray: new Array<string>(),
            modernStartPageEnabled: false,
            modernStreamStartPageEnabled: false,
            modernVisioStartPageEnabled: false,
            modernLoopStartPageEnabled: false,
            modernWhiteboardStartPageEnabled: false,
            modernDesignerStartPageEnabled: false,
            modernClipChampStartPageEnabled: false,
            modernStartPageRootPath: null,
            modernTemplatesAppsArray: new Array<string>(),
            modernTemplatesPageEnabled: false,
            myContentEnabled: false,
            myContentGridViewEnabled: false,
            myContentUsqEnabled: false,
            m365AppsPageRootPath: null,
            nativeSearchInHeaderEnabled: false,
            newConsumerExperienceEnabled: false,
            newConsumerOverride: false,
            newMruEmptyStateEnabled: false,
            newUserShelfEnabled: false,
            newUserShelfOverride: false,
            northStarEnabled: false,
            officeHomeFloodgate: false,
            officeStartUsedThirdPartyAppsEnabled: false,
            oneDriveApisEnabled: false,
            oneDriveUploadEnabled: false,
            otherFlights: '',
            outgoingLoginHintEnabled: false,
            parkEnabled: false,
            peopleCardsEnabled: false,
            peopleListViewEnabled: false,
            pinningPeopleEnabled: false,
            powerBiCreateEnabled: false,
            prefetchDataEnabled: false,
            pwaEnabled: false,
            quickAccessV1Enabled: false,
            quickAccessV1GridViewEnabled: false,
            quickAccessV1CustomPivotEnabled: false,
            reactAppToggleEnabled: false,
            recommendedDeepLinkEnabled: false,
            recommendedFloodgate: false,
            recommendedReact: false,
            recommendedTeamsLinkEnabled: false,
            redirectWhenSignedOutFromWorkloads: true,
            reloadWhenAuthExpired: true,
            reloadWhenPolicyCheckFailed: true,
            reloadWhenReauthRequired: true,
            remoteModulesEnabled: false,
            replacePinActionEnabled: false,
            scenarioId: null,
            scheduleEnabled: false,
            secondaryTokensEnabled: false,
            secondRunSREConsumerEnabled: false,
            sharedServicesEnabled: false,
            sharePointEnabled: false,
            shareTokenClaimsEnabled: false,
            shareToTeamsEnabled: false,
            shellDocumentTitleEnabledInFluid: false,
            shellFeedbackEnabled: false,
            shellPinEnabled: false,
            showA2HSPrompt: false,
            showBusinessApps: false,
            showFeedback: false,
            showFloodgate: false,
            showFolders: false,
            showPwaFeelingsSurvey: false,
            showSearch: false,
            sideloadAppButtonEnabled: false,
            surfaceExpEnabled: false,
            spfxAppEnabled: false,
            storeAppEnabled: false,
            startPageAppsArray: new Array<string>(),
            startPageRootPath: null,
            stickyPagesEnabled: false,
            suggestedActionsEnabled: false,
            supportCentralEnabled: false,
            svgAppIconsEnabled: false,
            swayCreateNewEnabled: false,
            teachingUiEnabled: false,
            teachingUiReactEnabled: false,
            teachingUiReactNewUserEnabled: false,
            templatePageRootPath: null,
            templatesEnabled: false,
            themeOverridesEnabled: false,
            thumbnailEndpointEnabled: false,
            threeSRdlFeedExperiment: '',
            threeSRdlFlightHeader: '',
            toDoM365AppEnabled: false,
            unifiedHostPath: '',
            unityEnabled: false,
            unityCalloutEnabled: false,
            universalMeControlPWAEnabled: false,
            universalMeControlSafetyFlightEnabled: false,
            upgradeMosAppEnabled: false,
            uploadV2Enabled: false,
            streamUploadV2Enabled: false,
            userConsentCallEnabled: false,
            userPreferenceEnabled: false,
            useVroomForOneDriveApis: false,
            visualAppsModuleEnabled: false,
            visualBackgroundEnabled: false,
            visualBackgroundGradientEnabled: false,
            visualCreateCenterModuleEnabled: false,
            visualEnabled: false,
            visualFeedModuleEnabled: false,
            visualMyContentModuleEnabled: false,
            visualSettingEnabled: false,
            wacExcelJsApiV2Enabled: false,
            wacHostAppsArray: new Array<string>(),
            wacHostPrebootAppsArray: new Array<string>(),
            wacHostTimeoutTeardownEnabled: false,
            wacHostEnabled: false,
            wacHostControlFlight: false,
            wacHostHoverPreloadEnabled: false,
            wacHostPagePath: '',
            wacSharingLinkEnabled: false,
            wacStartPageEnabled: false,
            wacHostExternalLinkEnabled: false,
            wacHostOrgTemplatesEnabled: false,
            wacHostGetAuthTokenEnabled: false,
            wacHostGetCustomAuthConfigEnabled: false,
            wacHostHoverPrebootAppsArray: new Array<string>(),
            wacHostHoverPrebootControlFlight: false,
            wacWordJsApiV2Enabled: false,
            wacHostMoveToFolderEnabled: false,
            wbShowStoragePercentageEnabled: false,
            webVitalsEnabled: false,
            welcomeBadgeEnabled: false,
            welcomeBadgeV2Enabled: false,
            whiteboardCreateEnabled: false,
            workloadPublicDataEnabled: false,
            yammerCreateEnabled: false,
            yammerM365AppEnabled: false,
            claimsChallengeEnabled: false,
            m365StringLogoEnabled: false,
            m365MobileDesktopRebrandEnabled: false,
            portalPreloadEnabled: false,
            announcementEnabled: false,
            useUSQEnabled: false,
            showEnforcementEnabled: false,
            hybridAuthEnabled: false
        };

        if (this.capabilities.featureFlagsEnabled) {
            this.updateWithOverrides();
        }

        this.capabilities.oneDriveUploadEnabled = this.capabilities.oneDriveUploadEnabled && deviceDetector.isDesktopDevice();
        this.capabilities.createDocumentSupported = !deviceDetector.isMobileDevice() && !deviceDetector.isTabletDevice(PlatformType.AndroidTablet);
        this.capabilities.isPwaInUwp = this.capabilities.pwaEnabled && deviceDetector.isUwp();
        this.capabilities.isPwaInWebView2 = this.capabilities.pwaEnabled && deviceDetector.isWebView2();
        this.capabilities.disableAnimation = this.capabilities.pwaEnabled;
        this.capabilities.mobileContextMenusEnabled = deviceDetector.isMobileDevice();
        this.capabilities.leftNavReact = this.capabilities.leftNavReact && !deviceDetector.isMobileDevice();
        this.capabilities.appBarEnabled = this.capabilities.appBarEnabled && !deviceDetector.isMobileDevice();
        this.capabilities.suggestedActionsEnabled = this.capabilities.suggestedActionsEnabled && !deviceDetector.isMobileDevice();
        this.capabilities.universalMeControlPWAEnabled = this.capabilities.universalMeControlSafetyFlightEnabled && this.capabilities.universalMeControlPWAEnabled;
        this.capabilities.mobileCustomSearchEnabled = deviceDetector.isMobileDevice() && !this.capabilities.nativeSearchInHeaderEnabled;
        this.capabilities.visualEnabled = !deviceDetector.isMobileDevice() && this.capabilities.visualEnabled;

        // Content actions
        if (!this.capabilities.contentActionsEnabled) {
            this.capabilities.addToDoEnabled = false;
            this.capabilities.compliantShareApiEnabled = false;
            this.capabilities.contextMenu20Enabled = false;
            this.capabilities.convertToPdfEnabled = false;
            this.capabilities.scheduleEnabled = false;
            this.capabilities.shareTokenClaimsEnabled = false;
            this.capabilities.shareToTeamsEnabled = false;
            this.capabilities.createSharingLinkApiEnabled = false;
            this.capabilities.contentActionsOHomeEnabled = false;
        }

        // Preinstalled MetaOs Apps
        if (!this.capabilities.m365AppsEnabled) {
            this.capabilities.m365TestAppEnabled = false;
            this.capabilities.parkEnabled = false;
            this.capabilities.spfxAppEnabled = false;
            this.capabilities.storeAppEnabled = false;
            this.capabilities.toDoM365AppEnabled = false;
            this.capabilities.yammerM365AppEnabled = false;
        }

        if (isAppShell()) {
            this.capabilities.clientTokenFetchEnabled = true;
        }

        if (this.capabilities.wacHostAppsArray) {
            this.capabilities.wacHostApps = new Set<FileType>(this.capabilities.wacHostAppsArray.map(app =>
                FileItem.fileTypeNameToFileType(app)));
        }

        if (this.capabilities.startPageAppsArray) {
            this.capabilities.startPageApps = new Set<FileType>(this.capabilities.startPageAppsArray.map(app =>
                FileItem.fileTypeNameToFileType(app)));
        }

        if (this.capabilities.modernStartAppsArray) {
            this.capabilities.modernStartApps = new Set<FileType>(this.capabilities.modernStartAppsArray.map(app =>
                FileItem.fileTypeNameToFileType(app)));
            this.capabilities.modernStartApps.add(FileType.Visio);
        }

        if (this.capabilities.wacHostPrebootAppsArray) {
            this.capabilities.wacHostPrebootApps = new Set<FileType>(this.capabilities.wacHostPrebootAppsArray.map(app =>
                FileItem.fileTypeNameToFileType(app)));
        }

        if (this.capabilities.wacHostHoverPrebootAppsArray) {
            this.capabilities.wacHostHoverPrebootApps = new Set<FileType>(this.capabilities.wacHostHoverPrebootAppsArray.map(app =>
                FileItem.fileTypeNameToFileType(app)));
        }

        this.openWithUIHost = webPwaService.isStandalone.pipe(
            map(isWebPwa => this.capabilities.wacHostEnabled || (this.capabilities.clientPwasEnabled && isWebPwa))
        );

        this.openWithUIHost.subscribe(enabled => this.shouldOpenWithUIHost = enabled);

        if (typeof this.capabilities.templatesEnabled === 'undefined') {
            // In the PWA, it's possible the templatesEnabled setting is not in a previously cached config API response.
            // To make sure we don't disable templates in that case, check that the page is on office.com WW. Templates are
            // only disabled on sovereign environments (ending in .us, .mil, .de).
            // TODO: remove this if block once this change is stable in prod.
            const hostname = windowService.nativeWindow.location.hostname;
            this.capabilities.templatesEnabled = hostname && hostname.length > 3 && hostname.lastIndexOf('.com') === hostname.length - 4;
        }

        // Enable EdgeWorth on WebView2 version of HWA, but disable on Edge Spartan version of HWA
        this.capabilities.edgeworthEnabled = this.capabilities.edgeworthEnabled && 'Proxy' in windowService.nativeWindow && (!this.capabilities.isPwaInUwp || this.capabilities.isPwaInWebView2);
    }

    public wacHostHoverPrebootEnabledForFileType(fileType: FileType): boolean {
        const wacHostHoverPrebootApps = this.capabilities.wacHostHoverPrebootApps;
        return !!wacHostHoverPrebootApps && wacHostHoverPrebootApps.has(fileType);
    }

    // TODO: Move to wac content service once DI issue is resolved
    public wacHostEnabledForFileType(fileType: FileType): boolean {
        const wacHostApps = this.capabilities.wacHostApps;
        return this.wacHostEnabled() && (!!wacHostApps && wacHostApps.has(fileType));
    }

    public wacHostEnabled(): boolean {
        return this.shouldOpenWithUIHost;
    }

    public getWorkload(): WorkloadQueryParameter {
        if (this.capabilities.pwaEnabled) {
            return WorkloadQueryParameter.Pwa;
        }
        if (this.capabilities.isEmbedMode) {
            return WorkloadQueryParameter.Embed;
        }
        return WorkloadQueryParameter.OfficeHome;
    }

    private updateWithOverrides() {
        const flagsToIgnore = new Set([
            'featureFlagsEnabled',
            'disableAnimation',
            'createDocumentSupported'
        ]);

        const newConfig = this.featureManagerService.registerFlagsAndGetConfig(
            FeatureManagerService.parseBooleanFlagsAndFilter(this.capabilities, flagsToIgnore)
        );

        const keys = Object.keys(newConfig);
        for (const key of keys) {
            this.capabilities[key] = newConfig[key];
        }
    }
}
